import { defineMessages } from "react-intl";

import TourDescriptor from "@app/component/flows/types/TourDescriptor";
import waitForElement from "@app/component/flows/utils/waitForElement";

import { checkForCompaniesWithEmail, closeComposeModal } from "./common";

const messages = defineMessages({
  step01: {
    id: "guides.sendingEmailsViaMap.tour.step01",
    defaultMessage: "Sending Emails via Map Lasso",
  },
  step01Description: {
    id: "guides.sendingEmailsViaMap.tour.step01.description",
    defaultMessage:
      "Use this method when you want to target contacts geographically or visually on the Map.",
  },
  step02: {
    id: "guides.sendingEmailsViaMap.tour.step02",
    defaultMessage: "Sending Emails via Map Lasso",
  },
  step02Description: {
    id: "guides.sendingEmailsViaMap.tour.step02.description",
    defaultMessage: "Go to the Map in the Navigation Menu.",
  },
  step03: {
    id: "guides.sendingEmailsViaMap.tour.step03",
    defaultMessage: "Lasso Tool",
  },
  step03Description: {
    id: "guides.sendingEmailsViaMap.tour.step03.description",
    defaultMessage: "Select the Lasso tool from the toolbar on the right side of the screen.",
  },
  step04: {
    id: "guides.sendingEmailsViaMap.tour.step04",
    defaultMessage: "Lasso Tool",
  },
  step04Description: {
    id: "guides.sendingEmailsViaMap.tour.step04.description",
    defaultMessage: "Draw around the pins you want to email.",
  },
  step05: {
    id: "guides.sendingEmailsViaMap.tour.step05",
    defaultMessage: "Email Button",
  },
  step05Description: {
    id: "guides.sendingEmailsViaMap.tour.step05.description",
    defaultMessage:
      "You need to select records with an email. Otherwise this button will be disabled.{br}Please select suitable records, then click the button.",
  },
  step06: {
    id: "guides.sendingEmailsViaMap.tour.step06",
    defaultMessage: "Email Button",
  },
  step06Description: {
    id: "guides.sendingEmailsViaMap.tour.step06.description",
    defaultMessage: "Click the Email button in the action bar.",
  },
  step07: {
    id: "guides.sendingEmailsViaMap.tour.step07",
    defaultMessage: "Email Button",
  },
  step07Description: {
    id: "guides.sendingEmailsViaMap.tour.step07.description",
    defaultMessage: "Select Compose Email",
  },
  step08: {
    id: "guides.sendingEmailsViaMap.tour.step08",
    defaultMessage: "",
  },
  step08Description: {
    id: "guides.sendingEmailsViaMap.tour.step08.description",
    defaultMessage: "",
  },
  step09: {
    id: "guides.sendingEmailsViaMap.tour.step09",
    defaultMessage: "No emails",
  },
  step09Description: {
    id: "guides.sendingEmailsViaMap.tour.step09.description",
    defaultMessage:
      "In case some selected pins have no emails, this modal will show up. Click Continue to proceed.",
  },
  step10: {
    id: "guides.sendingEmailsViaMap.tour.step10",
    defaultMessage: "Deals Selected",
  },
  step10Description: {
    id: "guides.sendingEmailsViaMap.tour.step10.description",
    defaultMessage:
      'Select who you want to email in this modal. Selecting "People associated with Companies" will also provide the option to only send the email to the Primary Contact.{br}{br}Pick either option and click Continue.',
  },
  step11: {
    id: "guides.sendingEmailsViaMap.tour.step11",
    defaultMessage: "Multiple Record types",
  },
  step11Description: {
    id: "guides.sendingEmailsViaMap.tour.step11.description",
    defaultMessage:
      'Select who you want to email in this modal. Selecting "People associated with Companies" will also provide the option to only send the email to the Primary Contact.{br}{br}Pick either option and click Continue.',
  },
  step12: {
    id: "guides.sendingEmailsViaMap.tour.step12",
    defaultMessage: "Compose your email",
  },
  step12Description: {
    id: "guides.sendingEmailsViaMap.tour.step12.description",
    defaultMessage:
      "👤 Confirm the recipients in To{br}✉️ Add a Subject line{br}✍️ Write your Message - You can use the toolbar icons at the bottom left to insert a hyperlink, file, resource or dynamic variable.",
  },
  step13: {
    id: "guides.sendingEmailsViaMap.tour.step13",
    defaultMessage: "Send your email",
  },
  step13Description: {
    id: "guides.sendingEmailsViaMap.tour.step13.description",
    defaultMessage:
      "If you have disabled the Open Activity Form setting, the button will show Send and Create.{br}If enabled or required fields exist, click Next to complete the activity form and then finalize.",
  },
  step14: {
    id: "guides.sendingEmailsViaMap.tour.step14",
    defaultMessage: "Tour Complete",
  },
  step14Description: {
    id: "guides.sendingEmailsViaMap.tour.step14.description",
    defaultMessage: "🎉 Congratulations! You've completed this tour.",
  },
});

export const sendingViaMapTourConfiguration: TourDescriptor = {
  id: "mmc-sending-emails-via-map",
  steps: [
    {
      id: "intro",
      description: messages.step01Description,
      onBefore: async ({ store }) => checkForCompaniesWithEmail(store),
      title: messages.step01,
    },
    {
      id: "lassoing-1",
      description: messages.step03Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "left",
      selector: "[data-tool-btn=lasso]",
      title: messages.step03,
      waitForEvent: {
        eventType: "click",
        selector: "[data-tool-btn=lasso]",
      },
    },
    {
      id: "lassoing-1",
      description: messages.step04Description,
      title: messages.step04,
    },
    {
      // dummy step, waiting for user to draw a lasso
      id: "email-button-1",
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      waitForEvent: {
        eventType: "internal",
        selector: "Using: Map -> Lasso Tool -> Created Shape",
      },
    },
    {
      id: "email-button-2",
      description: messages.step05Description,
      onAfter: async () => {
        return {
          action: "navigate",
          stepId: "email-button-4",
        };
      },
      onBefore: async () => {
        // waiting for the email button to appear
        await new Promise<void>((resolve) =>
          waitForElement("[data-selection-button=bulk-email]", resolve, {
            failureCallback: resolve,
          })
        );

        // is it enabled?
        const enabledButtonSelector = "[data-selection-button=bulk-email]:not([disabled])";
        let enabledEmailButton = document.querySelector(enabledButtonSelector);
        if (enabledEmailButton) {
          return {
            action: "navigate",
            stepId: "email-button-3",
          };
        }

        // maybe it's in loading state and disabled because of that?
        const loadingEmailButton = document.querySelector(
          "[data-selection-button=bulk-email].ant-btn-loading"
        );

        // if it is disabled and not loading, it's just disabled
        // nothing we can do here
        if (!loadingEmailButton) {
          return;
        }

        // otherwise let's wait for loading to complete
        await new Promise<void>((resolve) =>
          waitForElement("[data-selection-button=bulk-email]:not(.ant-btn-loading)", resolve, {
            failureCallback: resolve,
          })
        );

        // so is it enabled now?
        enabledEmailButton = document.querySelector(enabledButtonSelector);
        if (enabledEmailButton) {
          return {
            action: "navigate",
            stepId: "email-button-3",
          };
        }
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[data-selection-button=bulk-email]",
      title: messages.step05,
      waitForEvent: {
        eventType: "click",
        selector: "[data-selection-button=bulk-email]",
      },
    },
    {
      id: "email-button-3",
      description: messages.step06Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "bottom",
      selector: "[data-selection-button=bulk-email]",
      title: messages.step06,
      waitForEvent: {
        eventType: "click",
        selector: "[data-selection-button=bulk-email]",
      },
    },
    {
      id: "email-button-4",
      description: messages.step07Description,
      onBefore: async () => {
        // just letting the dropdown animation to complete
        return new Promise((resolve) => setTimeout(resolve, 100));
      },
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "right",
      selector: "[data-email-button-item=compose]",
      title: messages.step07,
      waitForEvent: {
        eventType: "click",
        selector: "[data-email-button-item=compose]",
      },
    },
    {
      id: "email-button-5",
      description: messages.step08Description, // dummy, think about making description field optional
      onBefore: async () => {
        await new Promise<void>((resolve) =>
          waitForElement("[class*=mmc-js-modal-email]", resolve, {
            failureCallback: resolve,
            timeout: 1000,
          })
        );

        if (document.querySelector("[class*=mmc-js-modal-email-emails-missing]")) {
          return { action: "navigate", stepId: "email-modal-emails-missing" };
        }

        if (document.querySelector("[class*=mmc-js-modal-email-multiple-entities]")) {
          return { action: "navigate", stepId: "email-modal-multiple-entities" };
        }

        if (document.querySelector("[class*=mmc-js-modal-email-deals-selected]")) {
          return { action: "navigate", stepId: "email-modal-deals-selected" };
        }

        return { action: "navigate", stepId: "email-compose" };
      },
    },
    {
      id: "email-modal-emails-missing",
      description: messages.step09Description,
      onAfter: async () => {
        return Promise.resolve({ action: "navigate", stepId: "email-compose" });
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[class*=mmc-js-modal-email-emails-missing] .ant-btn-primary",
      title: messages.step09,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=mmc-js-modal-email-emails-missing] .ant-btn-primary",
      },
    },
    {
      id: "email-modal-deals-selected",
      description: messages.step10Description,
      onAfter: async () => {
        return Promise.resolve({ action: "navigate", stepId: "email-compose" });
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[class*=mmc-js-modal-email-deals-selected] .ant-btn-primary",
      title: messages.step10,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=mmc-js-modal-email-deals-selected] .ant-btn-primary",
      },
    },
    {
      id: "email-modal-multiple-entities",
      description: messages.step11Description,
      onAfter: async () => {
        return Promise.resolve({ action: "navigate", stepId: "email-compose" });
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "right",
      selector: "[class*=mmc-js-modal-email-multiple-entities] .ant-btn-primary",
      title: messages.step11,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=mmc-js-modal-email-multiple-entities] .ant-btn-primary",
      },
    },
    {
      id: "email-compose",
      description: messages.step12Description,
      title: messages.step12,
    },
    {
      id: "email-send",
      description: messages.step13Description,
      options: {
        showBackground: false,
      },
      selector: "[class*=EmailComposeModal_actionContainer] .ant-btn-primary",
      title: messages.step13,
    },
    {
      id: "completed",
      description: messages.step14Description,
      onAfter: closeComposeModal,
      title: messages.step14,
    },
  ],
};
